import { CategorySettings } from '../types';

export interface AppSettings {
  autoSave: boolean;
  autoSaveInterval: number; // 自动保存间隔(秒)
  darkMode: boolean;
  autoStartup: boolean; // 网页版中无效，保留兼容性
  autoCheckUpdate: boolean; // 自动检查更新
  shortcutKeys: {
    newDocument: string;
    saveDocument: string;
    togglePreview: string;
    searchDocuments: string;
  };
  categorySettings: CategorySettings;
  availableCategories: string[];
  // 网页版特有设置
  webSettings: {
    enableNotifications: boolean;
    enableOfflineMode: boolean;
    autoSyncInterval: number; // 自动同步间隔(分钟)
    maxOfflineDocuments: number;
    enableServiceWorker: boolean;
    compressImages: boolean;
    enableAnalytics: boolean;
    language: 'zh-CN' | 'en-US';
    fontSize: number;
    lineHeight: number;
    editorTheme: 'default' | 'github' | 'monokai' | 'solarized';
  };
}

export class SettingsManager {
  private static readonly SETTINGS_KEY = 'daily-notes-settings';
  private static readonly BACKUP_KEY = 'daily-notes-settings-backup';

  private static defaultSettings: AppSettings = {
    autoSave: true,
    autoSaveInterval: 10,
    darkMode: false,
    autoStartup: false, // 网页版中无效
    autoCheckUpdate: true,
    shortcutKeys: {
      newDocument: 'Ctrl+N',
      saveDocument: 'Ctrl+S',
      togglePreview: 'Ctrl+P',
      searchDocuments: 'Ctrl+F',
    },
    categorySettings: {
      '默认': { color: 'blue', visible: true },
      '工作': { color: 'red', visible: true },
      '学习': { color: 'green', visible: true },
      '生活': { color: 'orange', visible: true },
      '想法': { color: 'purple', visible: true },
    },
    availableCategories: ['默认', '工作', '学习', '生活', '想法'],
    webSettings: {
      enableNotifications: true,
      enableOfflineMode: true,
      autoSyncInterval: 5, // 5分钟
      maxOfflineDocuments: 100,
      enableServiceWorker: true,
      compressImages: true,
      enableAnalytics: false,
      language: 'zh-CN',
      fontSize: 14,
      lineHeight: 1.6,
      editorTheme: 'default'
    }
  };

  static getSettings(): AppSettings {
    try {
      const data = localStorage.getItem(this.SETTINGS_KEY);
      if (!data) return this.defaultSettings;

      const settings = JSON.parse(data);
      // 合并默认设置，确保新增的设置项有默认值
      return this.mergeWithDefaults(settings);
    } catch (error) {
      // 尝试从备份恢复
      return this.restoreFromBackup();
    }
  }

  private static mergeWithDefaults(settings: any): AppSettings {
    return {
      ...this.defaultSettings,
      ...settings,
      webSettings: {
        ...this.defaultSettings.webSettings,
        ...(settings.webSettings || {})
      },
      shortcutKeys: {
        ...this.defaultSettings.shortcutKeys,
        ...(settings.shortcutKeys || {})
      },
      categorySettings: {
        ...this.defaultSettings.categorySettings,
        ...(settings.categorySettings || {})
      }
    };
  }

  static saveSettings(settings: AppSettings): void {
    try {
      // 创建备份
      this.createBackup();

      // 保存设置
      localStorage.setItem(this.SETTINGS_KEY, JSON.stringify(settings));

      // 触发设置变化事件
      window.dispatchEvent(new CustomEvent('settingsChanged', { detail: settings }));

      // 网页版特有：应用一些设置
      this.applyWebSettings(settings);
    } catch (error) {
      throw error;
    }
  }

  private static createBackup(): void {
    try {
      const currentSettings = localStorage.getItem(this.SETTINGS_KEY);
      if (currentSettings) {
        localStorage.setItem(this.BACKUP_KEY, currentSettings);
      }
    } catch (error) {
      // 静默处理备份创建失败
    }
  }

  private static restoreFromBackup(): AppSettings {
    try {
      const backup = localStorage.getItem(this.BACKUP_KEY);
      if (backup) {
        const settings = JSON.parse(backup);
        return this.mergeWithDefaults(settings);
      }
    } catch (error) {
      // 静默处理备份恢复失败
    }
    return this.defaultSettings;
  }

  private static applyWebSettings(settings: AppSettings): void {
    // 应用主题
    document.documentElement.setAttribute('data-theme', settings.darkMode ? 'dark' : 'light');

    // 应用字体设置
    document.documentElement.style.setProperty('--font-size', `${settings.webSettings.fontSize}px`);
    document.documentElement.style.setProperty('--line-height', settings.webSettings.lineHeight.toString());

    // 应用语言设置
    document.documentElement.lang = settings.webSettings.language;

    // 请求通知权限
    if (settings.webSettings.enableNotifications && 'Notification' in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission();
      }
    }
  }

  // 在应用启动时调用，应用当前保存的设置到DOM（字体大小、行高、主题、语言等）
  static applyCurrentSettings(): void {
    try {
      const settings = this.getSettings();
      this.applyWebSettings(settings);
    } catch { }
  }

  static resetSettings(): void {
    try {
      localStorage.removeItem(this.SETTINGS_KEY);
      localStorage.removeItem(this.BACKUP_KEY);

      // 重新应用默认设置
      this.applyWebSettings(this.defaultSettings);

      // 触发设置重置事件
      window.dispatchEvent(new CustomEvent('settingsReset'));
    } catch (error) {
      // 静默处理设置重置失败
    }
  }

  static addCategory(categoryName: string, color: string = 'blue'): void {
    const settings = this.getSettings();
    if (!settings.availableCategories.includes(categoryName)) {
      settings.availableCategories.push(categoryName);
      settings.categorySettings[categoryName] = { color, visible: true };
      this.saveSettings(settings);
    }
  }

  static removeCategory(categoryName: string): void {
    const settings = this.getSettings();
    settings.availableCategories = settings.availableCategories.filter(cat => cat !== categoryName);
    delete settings.categorySettings[categoryName];
    this.saveSettings(settings);
  }

  static updateCategoryColor(categoryName: string, color: string): void {
    const settings = this.getSettings();
    if (settings.categorySettings[categoryName]) {
      settings.categorySettings[categoryName].color = color;
      this.saveSettings(settings);
    }
  }

  static toggleCategoryVisibility(categoryName: string): void {
    const settings = this.getSettings();
    if (settings.categorySettings[categoryName]) {
      settings.categorySettings[categoryName].visible = !settings.categorySettings[categoryName].visible;
      this.saveSettings(settings);
    }
  }

  // 切换夜间模式的便捷方法
  static toggleDarkMode(): void {
    const settings = this.getSettings();
    settings.darkMode = !settings.darkMode;
    this.saveSettings(settings);
  }

  // 网页版特有方法

  // 导出设置
  static exportSettings(): Blob {
    const settings = this.getSettings();
    const exportData = {
      settings,
      exportTime: new Date().toISOString(),
      version: '1.0'
    };

    const jsonData = JSON.stringify(exportData, null, 2);
    return new Blob([jsonData], { type: 'application/json' });
  }

  // 导入设置
  static async importSettings(file: File): Promise<boolean> {
    try {
      const text = await file.text();
      const importData = JSON.parse(text);

      if (importData.settings) {
        const mergedSettings = this.mergeWithDefaults(importData.settings);
        this.saveSettings(mergedSettings);
        return true;
      }

      throw new Error('Invalid settings file format');
    } catch (error) {
      return false;
    }
  }

  // 获取设置统计
  static getSettingsStats(): {
    totalCategories: number;
    enabledFeatures: string[];
    lastModified: string | null;
  } {
    const settings = this.getSettings();
    const enabledFeatures: string[] = [];

    if (settings.autoSave) enabledFeatures.push('自动保存');
    if (settings.darkMode) enabledFeatures.push('深色模式');
    if (settings.webSettings.enableNotifications) enabledFeatures.push('通知');
    if (settings.webSettings.enableOfflineMode) enabledFeatures.push('离线模式');
    if (settings.webSettings.enableServiceWorker) enabledFeatures.push('Service Worker');

    return {
      totalCategories: settings.availableCategories.length,
      enabledFeatures,
      lastModified: localStorage.getItem(this.SETTINGS_KEY + '_modified') || null
    };
  }

  // 检查设置完整性
  static validateSettings(): {
    isValid: boolean;
    errors: string[];
    warnings: string[];
  } {
    const settings = this.getSettings();
    const errors: string[] = [];
    const warnings: string[] = [];

    // 检查必需字段
    if (!settings.availableCategories || settings.availableCategories.length === 0) {
      errors.push('至少需要一个分类');
    }

    // 检查数值范围
    if (settings.autoSaveInterval < 1 || settings.autoSaveInterval > 300) {
      warnings.push('自动保存间隔建议在1-300秒之间');
    }

    if (settings.webSettings.fontSize < 10 || settings.webSettings.fontSize > 24) {
      warnings.push('字体大小建议在10-24px之间');
    }

    // 检查分类设置一致性
    for (const category of settings.availableCategories) {
      if (!settings.categorySettings[category]) {
        warnings.push(`分类"${category}"缺少设置信息`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  // 自动修复设置
  static autoFixSettings(): boolean {
    try {
      const settings = this.getSettings();
      let hasChanges = false;

      // 修复缺失的分类设置
      for (const category of settings.availableCategories) {
        if (!settings.categorySettings[category]) {
          settings.categorySettings[category] = { color: 'blue', visible: true };
          hasChanges = true;
        }
      }

      // 修复数值范围
      if (settings.autoSaveInterval < 1) {
        settings.autoSaveInterval = 10;
        hasChanges = true;
      }

      if (settings.webSettings.fontSize < 10) {
        settings.webSettings.fontSize = 14;
        hasChanges = true;
      }

      if (hasChanges) {
        this.saveSettings(settings);
      }

      return hasChanges;
    } catch (error) {
      return false;
    }
  }

  // 监听系统主题变化
  static watchSystemTheme(): void {
    if (window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

      const handleThemeChange = (e: MediaQueryListEvent) => {
        const settings = this.getSettings();
        if (settings.webSettings.language === 'zh-CN') {
          // 可以选择是否跟随系统主题
          // settings.darkMode = e.matches;
          // this.saveSettings(settings);
        }
      };

      mediaQuery.addEventListener('change', handleThemeChange);
    }
  }
}
